import { faBinoculars, faCashRegister, faEdit, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import useModalManager from '@hooks/useModalManager';
import { Badge, Blockquote, Button, InfoTooltip } from '@ui';
import { InlineSwitch } from '@ui/InlineSwitch';
import useCheckPermissions from 'hooks/useCheckPermissions';
import dynamic from 'next/dynamic';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useModal } from '../../../../hooks/useModal';
import { useClubSlug } from '../../../Clubs/hooks';
import { Column, DataTable, DataTableContainer, TableActionButton } from '../../../DataTable';
import Loader from '../../../Loader';
import { MetaComponent, NumberComponent } from '../../../UserInvoice/InvoiceList';
import { UserInvoice } from '../../../UserInvoice/types';
import { ClubMember } from '../../types';

const ClubMemberPreview = dynamic(() => import('../ClubMemberPreview'));
const PaymentAssistent = dynamic(() => import('../../../UserInvoice/PaymentAssistent/PaymentAssistent'));
const DunningAssistant = dynamic(() => import('../../../UserInvoice/DunningAssistant/DunningAssistant'));
const InvoiceForm = dynamic(() => import('../../../UserInvoice/InvoiceForm'));

/** Aging buckets in the order they are shown; matches the backend's keys. */
const BUCKETS = ['not_due', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'] as const;

interface OpenItemsSummary {
    payer: { id: number; name: string; debtor_number: string | null };
    is_payer: boolean;
    match_key: 'party_id' | 'debtor' | 'email' | 'none' | null;
    summary: {
        open_cents: number;
        invoice_count: number;
        overdue_cents: number;
        overdue_count: number;
        max_dunning_level: number;
        oldest_due_date: string | null;
        credits_cents: number;
    } | null;
    aging: Record<(typeof BUCKETS)[number], number> | null;
    family: Array<{
        id: number;
        name: string;
        is_group_head: boolean;
        open_cents: number;
        attribution: 'exact' | 'guessed' | 'unknown';
    }>;
    unattributed_cents?: number;
}

/** One open item row as returned by the list endpoint. */
type OpenItem = UserInvoice & {
    open_cents: number;
    days_overdue: number | null;
    dunning_level: number | null;
    for_members: Array<{ id: number; name: string }>;
};

interface Props {
    member: ClubMember;
}

const formatCents = (cents: number) => (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 });

/**
 * Everything financial about one member in one place: the balance with its aging and
 * family split on top, and the invoices themselves below — receivables by default,
 * the full history behind a switch.
 */
const ClubMemberOpenItems = ({ member }: Props) => {
    const { t } = useTranslation('member-management');
    const clubSlug = useClubSlug();
    const [setModal, resetModal] = useModal();
    const { addModal, removeModal } = useModalManager();
    const asyncListRef = useRef<any>(undefined);
    const [onlyOpen, setOnlyOpen] = useState(true);
    const { hasAccess: canCreateDunning } = useCheckPermissions({ clubSlug, permission: 'user_invoice.create' });

    const { data: summary, isLoading, mutate } = useSWR<OpenItemsSummary>(`club-member/${member.id}/open-items`);

    // A payment or a dunning changes both the rows and the figures above them.
    const reload = useCallback(() => {
        asyncListRef.current?.reload();
        void mutate();
    }, [mutate]);

    const handleShowPDF = (invoice: OpenItem) => {
        const popup = window.open(invoice.url, '_blank');
        if (popup != null) popup.focus();
    };

    const handleCapturePayment = useCallback(
        (invoice: OpenItem) =>
            setModal({
                title: t('clubMember.preview.openItems.capturePaymentTitle'),
                content: (
                    <PaymentAssistent
                        invoice={invoice}
                        callback={() => {
                            reload();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                onClose: () => reload()
            }),
        [t, reload, resetModal, setModal]
    );

    const handleShowInvoiceForm = useCallback(
        (invoice: OpenItem) =>
            setModal({
                title: t('clubMember.preview.openItems.editTitle'),
                content: (
                    <InvoiceForm
                        invoice={invoice}
                        callback={() => {
                            reload();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                size: 80
            }),
        [t, reload, resetModal, setModal]
    );

    const handleManageDunnings = useCallback(
        (invoice: OpenItem) =>
            addModal({
                title: t('clubMember.preview.openItems.dunningTitle'),
                content: (id) => (
                    <DunningAssistant
                        invoice={invoice}
                        callback={() => {
                            reload();
                            removeModal(id);
                        }}
                    />
                ),
                size: 60,
                show: true
            }),
        [t, reload, addModal, removeModal]
    );

    const columns: Column<OpenItem>[] = [
        {
            uid: 'number',
            name: t('clubMember.preview.openItems.columns.number'),
            renderer: (_, item) => <NumberComponent invoice={item} />
        },
        {
            uid: 'created_at',
            name: t('clubMember.preview.openItems.columns.data'),
            renderer: (_, item) => (
                <div className="tw-flex tw-flex-col tw-gap-1">
                    <MetaComponent invoice={item} />
                    {item.for_members.length > 0 && (
                        <span className="tw-text-xs tw-text-accents-6">
                            {item.for_members.map((forMember) => forMember.name).join(', ')}
                        </span>
                    )}
                </div>
            )
        },
        {
            uid: 'open_cents',
            name: t('clubMember.preview.openItems.columns.open'),
            renderer: (_, item) => (
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                    <Badge flat size="xs" color={item.days_overdue && item.days_overdue > 0 ? 'error' : 'secondary'}>
                        {formatCents(item.open_cents)} €
                    </Badge>
                    {!!item.days_overdue && item.days_overdue > 0 && (
                        <span className="tw-text-xs tw-text-error">
                            {t('clubMember.preview.openItems.daysOverdue', { days: item.days_overdue })}
                        </span>
                    )}
                    {!!item.dunning_level && (
                        <Badge flat size="xs" color="warning">
                            {t('clubMember.preview.openItems.dunningLevel', { level: item.dunning_level })}
                        </Badge>
                    )}
                </div>
            )
        },
        { uid: 'actions', name: t('clubMember.preview.openItems.columns.options'), align: 'end' }
    ];

    const emailMatchHint = summary?.match_key === 'email' && (
        <Blockquote color="warning" size="sm">
            {t('clubMember.preview.openItems.emailMatchHint')}
        </Blockquote>
    );

    const invoiceList = (
        <>
            {/* !h-auto drops DataTableContainer's own h-full so flex-1 governs the height,
                and the explicit min-height replaces a flex child's default min-height:auto:
                without it the table would neither shrink below its content nor, once the
                summary above grows, keep enough room to stay usable. The tab's own
                scroll container takes over from there. */}
            <div className="tw--mb-1 tw-flex tw-shrink-0 tw-items-center tw-justify-end tw-gap-2">
                <span className="tw-text-xs tw-text-accents-6">{t('clubMember.preview.openItems.onlyOpen')}</span>
                <InlineSwitch checked={onlyOpen} onChange={setOnlyOpen} />
            </div>

            <DataTableContainer className="!tw-h-auto tw-min-h-[20rem] tw-flex-1">
                <DataTable
                    // Remounts on the switch: DataTable feeds fetchUrl into useAsyncList's
                    // load function, which keeps the one it was created with, so changing
                    // the url alone would leave the list loading the previous mode.
                    key={onlyOpen ? 'open' : 'all'}
                    showStatistic
                    hideSearchBar
                    className="tw-h-full"
                    fetchUrl={`club-member/${member.id}/open-items/list?only_open=${onlyOpen ? 1 : 0}`}
                    columns={columns}
                    asyncListRef={asyncListRef}
                    tableProps={{ showSelectionCheckboxes: false, sticked: true, compact: false }}
                    rowActions={(item: OpenItem) => (
                        <TableActionButton.Container justify="flex-end">
                            <TableActionButton
                                icon={faBinoculars}
                                tooltip={t('clubMember.preview.openItems.showPdf')}
                                onPress={() => handleShowPDF(item)}
                            />
                            <TableActionButton
                                icon={faCashRegister}
                                tooltip={t('clubMember.preview.openItems.capturePayment')}
                                onPress={() => handleCapturePayment(item)}
                            />
                            {canCreateDunning && (
                                <TableActionButton
                                    color="warning"
                                    icon={faFileInvoiceDollar}
                                    tooltip={t('clubMember.preview.openItems.manageDunnings')}
                                    onPress={() => handleManageDunnings(item)}
                                />
                            )}
                            {!item.cancelled_at && (
                                <TableActionButton
                                    color="warning"
                                    icon={faEdit}
                                    tooltip={t('clubMember.preview.openItems.edit')}
                                    onPress={() => handleShowInvoiceForm(item)}
                                />
                            )}
                        </TableActionButton.Container>
                    )}
                />
            </DataTableContainer>
        </>
    );

    // Not the payer: the family's invoices are generated onto the group head, so any
    // balance shown here would be someone else's. Send the user there for the figures,
    // the list below still holds this member's own invoices and the family ones billing them.
    if (!isLoading && summary && !summary.is_payer) {
        return (
            <div className="tw-flex tw-h-full tw-flex-col tw-gap-3">
                <Blockquote>{t('clubMember.preview.openItems.notPayer', { name: summary.payer.name })}</Blockquote>
                <Button
                    size="xs"
                    color="secondary"
                    className="!tw-w-full"
                    onPress={() =>
                        setModal({
                            title: null,
                            content: <ClubMemberPreview clubMemberId={summary.payer.id} clubSlug={clubSlug} />,
                            show: true,
                            size: 80
                        })
                    }
                >
                    {t('clubMember.preview.openItems.showPayer', { name: summary.payer.name })}
                </Button>
                {emailMatchHint}
                {invoiceList}
            </div>
        );
    }

    return (
        <Loader isLoading={isLoading}>
            <div className="tw-flex tw-h-full tw-flex-col tw-gap-3">
                <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2">
                    <div className="tw-grid tw-grid-cols-3 tw-gap-2">
                        <div className="tw-card-flat tw-flex tw-flex-col !tw-p-3">
                            <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-uppercase tw-tracking-wide tw-text-accents-6">
                                {t('clubMember.preview.openItems.totalOpen')}
                                {/* The Mahngebühr of a Mahnung is not part of the open amount: a dunning is
                                    itself an invoice replicating the original's items, so counting it in
                                    would double the claim. */}
                                <InfoTooltip tooltip={t('clubMember.preview.openItems.dunningFeeHint')} />
                            </span>
                            <span
                                className={`tw-text-lg tw-font-bold tw-leading-tight ${
                                    summary?.summary?.open_cents ? 'tw-text-accents-9' : 'tw-text-success'
                                }`}
                            >
                                {formatCents(summary?.summary?.open_cents ?? 0)} €
                            </span>
                            <span className="tw-text-xs tw-text-accents-6">
                                {t('clubMember.preview.openItems.invoiceCount', {
                                    count: summary?.summary?.invoice_count ?? 0
                                })}
                            </span>
                        </div>
                        <div className="tw-card-flat tw-flex tw-flex-col !tw-p-3">
                            <span className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-accents-6">
                                {t('clubMember.preview.openItems.overdue')}
                            </span>
                            <span
                                className={`tw-text-lg tw-font-bold tw-leading-tight ${
                                    summary?.summary?.overdue_cents ? 'tw-text-error' : 'tw-text-accents-9'
                                }`}
                            >
                                {formatCents(summary?.summary?.overdue_cents ?? 0)} €
                            </span>
                            <span className="tw-text-xs tw-text-accents-6">
                                {summary?.summary?.oldest_due_date
                                    ? t('clubMember.preview.openItems.oldestDueDate', {
                                          date: summary.summary.oldest_due_date
                                      })
                                    : t('clubMember.preview.openItems.nothingOverdue')}
                            </span>
                        </div>
                        <div className="tw-card-flat tw-flex tw-flex-col !tw-p-3">
                            <span className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-accents-6">
                                {t('clubMember.preview.openItems.maxDunningLevel')}
                            </span>
                            <span className="tw-text-lg tw-font-bold tw-leading-tight tw-text-accents-9">
                                {summary?.summary?.max_dunning_level || '–'}
                            </span>
                            {!!summary?.summary?.credits_cents && (
                                <span className="tw-text-xs tw-text-accents-6">
                                    {t('clubMember.preview.openItems.credits', {
                                        amount: formatCents(summary.summary.credits_cents)
                                    })}
                                </span>
                            )}
                        </div>
                    </div>

                    {emailMatchHint}

                    {!!summary?.summary?.open_cents && (
                        <div className="tw-card-flat tw-flex tw-items-center tw-gap-3 !tw-p-2">
                            <span className="tw-shrink-0 tw-text-xs tw-uppercase tw-tracking-wide tw-text-accents-6">
                                {t('clubMember.preview.openItems.aging')}
                            </span>
                            <div className="tw-grid tw-flex-1 tw-grid-cols-5 tw-gap-2">
                                {BUCKETS.map((bucket) => (
                                    <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-1" key={bucket}>
                                        <span className="tw-text-xs tw-text-accents-6">
                                            {t(`clubMember.preview.openItems.buckets.${bucket}`)}
                                        </span>
                                        <span
                                            className={`tw-text-sm tw-font-bold ${
                                                bucket === 'd90_plus' && summary?.aging?.[bucket]
                                                    ? 'tw-text-error'
                                                    : 'tw-text-accents-9'
                                            }`}
                                        >
                                            {formatCents(summary?.aging?.[bucket] ?? 0)} €
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {summary && summary.family.length > 1 && (
                        <div className="tw-card-flat !tw-p-3">
                            <span className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-accents-6">
                                {t('clubMember.preview.openItems.family')}
                            </span>
                            <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-1 tw-text-sm">
                                {summary.family.map((familyMember) => (
                                    <div className="tw-flex tw-items-center tw-justify-between" key={familyMember.id}>
                                        <span className="tw-flex tw-items-center tw-gap-2">
                                            {familyMember.name}
                                            {familyMember.attribution === 'guessed' && (
                                                <Badge flat size="xs" color="light">
                                                    {t('clubMember.preview.openItems.attributionGuessed')}
                                                </Badge>
                                            )}
                                        </span>
                                        <span className="tw-font-bold">{formatCents(familyMember.open_cents)} €</span>
                                    </div>
                                ))}
                                {/* Shown rather than dropped: without it the per-person figures
                                    stop adding up to the total, which is the worse lie. */}
                                {!!summary.unattributed_cents && (
                                    <div className="tw-flex tw-items-center tw-justify-between tw-text-accents-6">
                                        <span>{t('clubMember.preview.openItems.unattributed')}</span>
                                        <span className="tw-font-bold">
                                            {formatCents(summary.unattributed_cents)} €
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {invoiceList}
            </div>
        </Loader>
    );
};

export default ClubMemberOpenItems;
